import { useState } from "react";
import { Check, MousePointerClick, X } from "lucide-react";
import { RichButton } from "@/components/RichText";

const SIZES = [
  ["sm", "Petit"],
  ["md", "Moyen"],
  ["lg", "Grand"],
];
const SHAPES = [
  ["pill", "Arrondi (pilule)"],
  ["rounded", "Coins arrondis"],
  ["square", "Carré"],
];
const ANIMS = [
  ["none", "Aucune"],
  ["pulse", "Pulsation"],
  ["bounce", "Rebond"],
  ["grow", "Zoom"],
  ["float", "Flottement"],
  ["shine", "Reflet brillant"],
];
const ALIGNS = [
  ["left", "⬅️ Gauche"],
  ["center", "↔️ Centré"],
  ["right", "➡️ Droite"],
];
const ICONS = [
  ["", "Aucune icône"],
  ["cart", "🛒 Panier"],
  ["download", "⬇️ Téléchargement"],
  ["card", "💳 Paiement"],
  ["whatsapp", "💬 WhatsApp / Chat"],
  ["phone", "📞 Téléphone"],
  ["mail", "✉️ Email"],
  ["play", "▶️ Lecture / Démo"],
  ["arrow", "➡️ Flèche"],
  ["link", "🔗 Lien externe"],
  ["check", "✅ Validation"],
  ["star", "⭐ Étoile"],
  ["gift", "🎁 Cadeau"],
  ["zap", "⚡ Éclair"],
  ["heart", "❤️ Cœur"],
  ["lock", "🔒 Cadenas"],
  ["sparkles", "✨ Étincelles"],
];

const EMPTY = {
  label: "",
  url: "",
  bg: "#0052FF",
  bg2: "#8B5CF6",
  fg: "#ffffff",
  size: "md",
  shape: "pill",
  anim: "pulse",
  d3: true,
  grad: false,
  icon: "cart",
  align: "left",
};

export default function ButtonInserter({ onInsert, testId = "btn" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const insert = () => {
    if (!form.label.trim() || !form.url.trim()) return;
    let url = form.url.trim();
    if (!/^(https?:\/\/|\/|mailto:|tel:)/i.test(url)) url = `https://${url}`;
    const opts = [
      `bg=${form.bg}`,
      form.grad ? `bg2=${form.bg2}` : null,
      `fg=${form.fg}`,
      `size=${form.size}`,
      `shape=${form.shape}`,
      `anim=${form.anim}`,
      `d3=${form.d3 ? 1 : 0}`,
      `grad=${form.grad ? 1 : 0}`,
      form.icon ? `icon=${form.icon}` : null,
      form.align !== "left" ? `align=${form.align}` : null,
    ].filter(Boolean);
    onInsert(`@[${form.label.trim()}](${url}|${opts.join("|")})`);
    setForm(EMPTY);
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`insert-button-btn-${testId}`}
        onClick={() => setOpen(true)}
        className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-fuchsia-600 hover:underline"
      >
        <MousePointerClick className="h-3.5 w-3.5" /> Insérer un bouton
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-3 rounded-xl border border-fuchsia-300 bg-fuchsia-50/60 p-3" data-testid={`button-panel-${testId}`}>
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          data-testid={`button-label-${testId}`}
          placeholder="Texte du bouton (ex : Télécharger maintenant)"
          value={form.label}
          onChange={upd("label")}
          className="h-10 rounded-lg border border-line px-3 text-sm focus:border-fuchsia-500 focus:outline-none"
        />
        <input
          data-testid={`button-url-${testId}`}
          placeholder="https://exemple.com"
          value={form.url}
          onChange={upd("url")}
          className="h-10 rounded-lg border border-line px-3 text-sm focus:border-fuchsia-500 focus:outline-none"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Fond
          <input type="color" data-testid={`button-bg-${testId}`} value={form.bg} onChange={upd("bg")} className="h-8 w-10 cursor-pointer rounded border border-line" />
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-bold text-slate-600">
          <input type="checkbox" data-testid={`button-grad-${testId}`} checked={form.grad} onChange={(e) => setForm((f) => ({ ...f, grad: e.target.checked }))} className="h-4 w-4 accent-fuchsia-600" />
          Dégradé animé
        </label>
        {form.grad && (
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
            2ᵉ couleur
            <input type="color" data-testid={`button-bg2-${testId}`} value={form.bg2} onChange={upd("bg2")} className="h-8 w-10 cursor-pointer rounded border border-line" />
          </label>
        )}
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Texte
          <input type="color" data-testid={`button-fg-${testId}`} value={form.fg} onChange={upd("fg")} className="h-8 w-10 cursor-pointer rounded border border-line" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Icône
          <select data-testid={`button-icon-${testId}`} value={form.icon} onChange={upd("icon")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {ICONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Taille
          <select data-testid={`button-size-${testId}`} value={form.size} onChange={upd("size")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Forme
          <select data-testid={`button-shape-${testId}`} value={form.shape} onChange={upd("shape")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {SHAPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Animation
          <select data-testid={`button-anim-${testId}`} value={form.anim} onChange={upd("anim")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {ANIMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Alignement
          <select data-testid={`button-align-${testId}`} value={form.align} onChange={upd("align")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {ALIGNS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-bold text-slate-600">
          <input type="checkbox" data-testid={`button-3d-${testId}`} checked={form.d3} onChange={(e) => setForm((f) => ({ ...f, d3: e.target.checked }))} className="h-4 w-4 accent-fuchsia-600" />
          Effet 3D
        </label>
      </div>

      <div className="rounded-lg bg-white px-3 py-3">
        <span className="mr-2 text-[11px] text-slate-400">Aperçu :</span>
        <div className={`mt-1 flex ${form.align === "center" ? "justify-center" : form.align === "right" ? "justify-end" : "justify-start"}`}>
          <RichButton href="#" cfg={{ ...form }}>{form.label || "Mon bouton"}</RichButton>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          data-testid={`button-insert-${testId}`}
          onClick={insert}
          disabled={!form.label.trim() || !form.url.trim()}
          className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> Insérer le bouton
        </button>
        <button
          type="button"
          data-testid={`button-cancel-${testId}`}
          onClick={() => setOpen(false)}
          className="flex items-center gap-1.5 rounded-lg border border-line px-4 py-2 text-xs font-bold text-slate-600"
        >
          <X className="h-3.5 w-3.5" /> Annuler
        </button>
      </div>
    </div>
  );
}
